import { useEffect, useMemo, useState } from 'react';

/**
 * Lightweight autocomplete for the inline WHERE / ORDER BY inputs on a
 * TableView tab. Designed to be small (~150 lines), keyboard-first, and
 * driver-aware. Not Monaco — those inputs are one-line filters and a full
 * editor would be wildly overkill, plus we'd mount it twice per open
 * TableView tab.
 *
 * What it suggests:
 * - WHERE: column names, then SQL operators (driver-specific list).
 * - ORDER BY: column names, plus ASC / DESC / `,` after a column.
 *
 * The state machine looks at the last meaningful token before the cursor
 * and classifies what comes next. No real AST; the inputs aren't complex
 * enough to need one. Inside string literals and quoted identifiers the
 * popup hides — we don't try to autocomplete inside `'foo'` or `"foo"`.
 */

export type AutocompleteMode = 'where' | 'orderby';

export interface ColumnInfo {
  name: string;
  type: string;
}

export type SuggestionKind = 'column' | 'operator' | 'keyword';

export interface Suggestion {
  /** Literal text inserted into the input. May include quoting. */
  text: string;
  /** Visible label in the popup (usually = text minus quoting). */
  display: string;
  kind: SuggestionKind;
  /** Type badge for column suggestions (varchar, number, …). */
  typeBadge?: string;
}

export interface AutocompleteResult {
  /** Suggestions to render in the popup. Empty = popup hidden. */
  suggestions: Suggestion[];
  /** Replace `[start, end]` of the input with the chosen suggestion's `text`. */
  replaceRange: { start: number; end: number };
  /** True once the typing-debounce window has elapsed. */
  visible: boolean;
}

const DEBOUNCE_MS = 150;

// ─── Operator sets (driver-specific) ──────────────────────────────────

const OPERATORS_BASE = ['=', '<', '<=', '>', '>='];
const OPERATORS_NULL = ['IS NULL', 'IS NOT NULL'];
const OPERATORS_RANGE = ['IN', 'NOT IN', 'BETWEEN'];

const OPERATORS_BY_DRIVER: Record<string, string[]> = {
  postgresql: [...OPERATORS_BASE, '!=', 'LIKE', 'ILIKE', 'NOT LIKE', ...OPERATORS_RANGE, ...OPERATORS_NULL],
  duckdb:     [...OPERATORS_BASE, '!=', 'LIKE', 'ILIKE', 'NOT LIKE', ...OPERATORS_RANGE, ...OPERATORS_NULL],
  mysql:      [...OPERATORS_BASE, '!=', 'LIKE', 'NOT LIKE', 'REGEXP', ...OPERATORS_RANGE, ...OPERATORS_NULL],
  mssql:      [...OPERATORS_BASE, '<>', 'LIKE', 'NOT LIKE', ...OPERATORS_RANGE, ...OPERATORS_NULL],
  oracle:     [...OPERATORS_BASE, '!=', 'LIKE', 'NOT LIKE', ...OPERATORS_RANGE, ...OPERATORS_NULL],
};

function operatorsFor(driverType: string): string[] {
  return OPERATORS_BY_DRIVER[driverType] ?? OPERATORS_BY_DRIVER.postgresql;
}

// ─── Identifier quoting (driver-specific) ─────────────────────────────

function quoteIdentifierIfNeeded(name: string, driverType: string): string {
  // Only quote when the name has spaces, special chars, or is a reserved
  // word risk (starts with a digit, contains an SQL operator char). Plain
  // snake_case identifiers stay bare to keep the input readable.
  const safe = /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(name);
  if (safe) return name;

  switch (driverType) {
    case 'mysql':   return `\`${name.replace(/`/g, '``')}\``;
    case 'mssql':   return `[${name.replace(/]/g, ']]')}]`;
    // Oracle folds unquoted names to UPPERCASE and quoted refs are
    // case-sensitive; our lowercased cache names must be uppercased when quoted.
    case 'oracle':  return `"${name.toUpperCase().replace(/"/g, '""')}"`;
    case 'postgresql':
    case 'duckdb':
    default:        return `"${name.replace(/"/g, '""')}"`;
  }
}

// ─── Tokenizer ────────────────────────────────────────────────────────

interface Token {
  type: 'ident' | 'operator' | 'keyword' | 'string' | 'number' | 'punct' | 'whitespace';
  value: string;
  start: number;
  end: number;
}

const KEYWORDS = new Set([
  'AND', 'OR', 'NOT', 'IS', 'NULL', 'IN', 'LIKE', 'ILIKE', 'BETWEEN',
  'REGEXP', 'ASC', 'DESC',
]);

/**
 * Tokenize the input from start to `end`. Lightweight — handles quoted
 * identifiers, string literals, numbers, operators, idents/keywords. Does
 * not validate; partial input is fine.
 */
function tokenize(input: string, end: number): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  const target = Math.min(end, input.length);

  while (i < target) {
    const ch = input[i];

    // Whitespace
    if (/\s/.test(ch)) {
      const start = i;
      while (i < target && /\s/.test(input[i])) i++;
      tokens.push({ type: 'whitespace', value: input.slice(start, i), start, end: i });
      continue;
    }

    // Quoted identifiers — Postgres/Oracle/DuckDB use ", MySQL `, MSSQL [...]
    if (ch === '"' || ch === '`') {
      const close = ch;
      const start = i;
      i++;
      while (i < target && input[i] !== close) i++;
      if (i < target) i++; // consume closing quote
      tokens.push({ type: 'ident', value: input.slice(start, i), start, end: i });
      continue;
    }
    if (ch === '[') {
      const start = i;
      i++;
      while (i < target && input[i] !== ']') i++;
      if (i < target) i++;
      tokens.push({ type: 'ident', value: input.slice(start, i), start, end: i });
      continue;
    }

    // String literal — `'...'` (we don't autocomplete inside)
    if (ch === '\'') {
      const start = i;
      i++;
      while (i < target && input[i] !== '\'') i++;
      if (i < target) i++;
      tokens.push({ type: 'string', value: input.slice(start, i), start, end: i });
      continue;
    }

    // Numbers
    if (/[0-9]/.test(ch)) {
      const start = i;
      while (i < target && /[0-9.]/.test(input[i])) i++;
      tokens.push({ type: 'number', value: input.slice(start, i), start, end: i });
      continue;
    }

    // Operators
    if ('=<>!'.includes(ch)) {
      const start = i;
      i++;
      while (i < target && '=<>!'.includes(input[i])) i++;
      tokens.push({ type: 'operator', value: input.slice(start, i), start, end: i });
      continue;
    }

    // Punctuation
    if ('(),'.includes(ch)) {
      tokens.push({ type: 'punct', value: ch, start: i, end: i + 1 });
      i++;
      continue;
    }

    // Identifier / keyword
    if (/[a-zA-Z_]/.test(ch)) {
      const start = i;
      while (i < target && /[a-zA-Z0-9_]/.test(input[i])) i++;
      const value = input.slice(start, i);
      const isKeyword = KEYWORDS.has(value.toUpperCase());
      tokens.push({
        type: isKeyword ? 'keyword' : 'ident',
        value,
        start,
        end: i,
      });
      continue;
    }

    // Unknown char — skip
    i++;
  }

  return tokens;
}

// ─── Context detection ────────────────────────────────────────────────

type Context =
  | 'expect-column'      // start, after AND/OR/(/, comma — a column name comes next
  | 'expect-operator'    // after a column — an operator comes next (or ASC/DESC for orderby)
  | 'expect-null-tail'   // after IS or IS NOT — expect NULL
  | 'inside-string'      // inside '...' — popup hidden
  | 'expect-value'       // after a binary operator — popup hidden, user types value
  | 'unknown';           // anything we don't recognize → no suggestions

interface DetectedContext {
  context: Context;
  /** Range of the partial token currently under cursor (what to replace). */
  replaceRange: { start: number; end: number };
  /** Lowercase prefix of the partial token; filter suggestions by it. */
  prefix: string;
}

function detectContext(input: string, cursor: number, mode: AutocompleteMode): DetectedContext {
  // Quick check — are we inside a string literal?
  // Count unescaped single quotes from start to cursor; odd = inside.
  let inString = false;
  for (let i = 0; i < cursor; i++) {
    if (input[i] === '\'' && input[i - 1] !== '\\') inString = !inString;
  }
  if (inString) {
    return { context: 'inside-string', replaceRange: { start: cursor, end: cursor }, prefix: '' };
  }

  const tokens = tokenize(input, cursor);

  // Identify the partial token (the one ending exactly at cursor and not
  // followed by whitespace). If the last non-whitespace token ends at the
  // cursor, that's the partial; we'll replace it with the suggestion.
  let partial: Token | null = null;
  for (let i = tokens.length - 1; i >= 0; i--) {
    if (tokens[i].type === 'whitespace') continue;
    if (tokens[i].end === cursor) partial = tokens[i];
    break;
  }

  // Significant tokens (excluding whitespace and the partial)
  const sig = tokens
    .filter((t) => t.type !== 'whitespace')
    .filter((t) => t !== partial);

  const last = sig[sig.length - 1];
  const prev = sig[sig.length - 2];

  const replaceRange = partial
    ? { start: partial.start, end: partial.end }
    : { start: cursor, end: cursor };
  const prefix = partial?.value.toLowerCase() ?? '';

  // Inside a quoted ident (start `"` or `[` or `` ` `` not yet closed) —
  // tokenizer just gave us an ident token; treat as expect-column with
  // the partial replacing the whole quoted form.
  // (Already captured by the ident-partial path above.)

  // Empty input or the very start
  if (sig.length === 0) {
    return { context: 'expect-column', replaceRange, prefix };
  }

  // After AND / OR / `(` / `,` → expect column
  if (last) {
    const lv = last.value.toUpperCase();
    if (
      (last.type === 'keyword' && (lv === 'AND' || lv === 'OR' || lv === 'NOT')) ||
      (last.type === 'punct' && (last.value === '(' || last.value === ','))
    ) {
      return { context: 'expect-column', replaceRange, prefix };
    }

    // After IS / IS NOT → expect NULL
    if (last.type === 'keyword' && lv === 'IS') {
      return { context: 'expect-null-tail', replaceRange, prefix };
    }
    if (
      last.type === 'keyword' && lv === 'NOT' &&
      prev?.type === 'keyword' && prev.value.toUpperCase() === 'IS'
    ) {
      return { context: 'expect-null-tail', replaceRange, prefix };
    }

    // After binary operator → user types value (no suggestions)
    if (last.type === 'operator') {
      return { context: 'expect-value', replaceRange, prefix };
    }

    // After LIKE / ILIKE / IN / BETWEEN / REGEXP → user types value
    if (last.type === 'keyword' && ['LIKE', 'ILIKE', 'BETWEEN', 'IN', 'REGEXP'].includes(lv)) {
      return { context: 'expect-value', replaceRange, prefix };
    }

    // After an ident → expect operator (or ASC/DESC if orderby)
    if (last.type === 'ident') {
      return { context: 'expect-operator', replaceRange, prefix };
    }

    // After a number / string (a value) — for WHERE we'd suggest AND/OR
    // but v1 keeps scope tight to "columns + operations". Drop to no-op.
    if (last.type === 'number' || last.type === 'string') {
      return { context: 'unknown', replaceRange, prefix };
    }
  }

  // The partial under cursor with no preceding sig tokens — column
  if (partial && sig.length === 0) {
    return { context: 'expect-column', replaceRange, prefix };
  }

  return { context: 'expect-column', replaceRange, prefix };
  void mode;
}

// ─── Suggestion builder ───────────────────────────────────────────────

function buildSuggestions(
  ctx: Context,
  prefix: string,
  mode: AutocompleteMode,
  columns: ColumnInfo[],
  driverType: string,
): Suggestion[] {
  if (ctx === 'inside-string' || ctx === 'expect-value' || ctx === 'unknown') {
    return [];
  }

  const out: Suggestion[] = [];

  if (ctx === 'expect-column') {
    for (const col of columns) {
      out.push({
        text: quoteIdentifierIfNeeded(col.name, driverType),
        display: col.name,
        kind: 'column',
        typeBadge: col.type,
      });
    }
  }

  if (ctx === 'expect-operator') {
    if (mode === 'orderby') {
      out.push(
        { text: 'ASC', display: 'ASC', kind: 'keyword' },
        { text: 'DESC', display: 'DESC', kind: 'keyword' },
        { text: ',', display: ',', kind: 'keyword' },
      );
    } else {
      for (const op of operatorsFor(driverType)) {
        out.push({ text: op, display: op, kind: 'operator' });
      }
    }
  }

  if (ctx === 'expect-null-tail') {
    out.push({ text: 'NULL', display: 'NULL', kind: 'keyword' });
  }

  // Filter by prefix (case-insensitive). For columns we match against
  // display (the bare name); for operators/keywords against display.
  if (prefix) {
    return out.filter((s) => s.display.toLowerCase().includes(prefix));
  }
  return out;
}

// ─── Hook ─────────────────────────────────────────────────────────────

/**
 * Returns the suggestions for the current input + cursor. Suggestions
 * appear after a brief debounce of typing — set `DEBOUNCE_MS` to taste.
 * Caller renders the popup and applies the chosen suggestion via the
 * returned `replaceRange`.
 */
export function useFilterAutocomplete(
  input: string,
  cursor: number,
  mode: AutocompleteMode,
  columns: ColumnInfo[],
  driverType: string,
): AutocompleteResult {
  const [visible, setVisible] = useState(false);

  // Debounce visibility — popup hides immediately on input change, then
  // re-appears 150ms after the last keystroke. Avoids the popup flicker
  // that comes with showing on every keystroke.
  useEffect(() => {
    setVisible(false);
    const t = setTimeout(() => setVisible(true), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [input, cursor]);

  return useMemo(() => {
    const detected = detectContext(input, cursor, mode);
    const suggestions = buildSuggestions(detected.context, detected.prefix, mode, columns, driverType);
    return {
      suggestions,
      replaceRange: detected.replaceRange,
      visible,
    };
  }, [input, cursor, mode, columns, driverType, visible]);
}

/**
 * Apply a suggestion to the input. Returns `{ next, cursor }` so the
 * caller can update its controlled state. After insertion, the cursor
 * lands at the end of the inserted text + a trailing space when the
 * suggestion expects more input after it (column → space → operator
 * comes next; operator → space → value comes next; etc.).
 */
export function applySuggestion(
  input: string,
  range: { start: number; end: number },
  suggestion: Suggestion,
): { next: string; cursor: number } {
  // Add a trailing space so the user can keep typing without manually
  // moving past the inserted token. Skip the space when the next
  // expected position is "value" (operators/LIKE/IN) — the user will
  // type a literal directly.
  const trailing = suggestion.kind === 'keyword' && suggestion.text === ',' ? ' ' : ' ';

  const next = input.slice(0, range.start) + suggestion.text + trailing + input.slice(range.end);
  const cursor = range.start + suggestion.text.length + trailing.length;
  return { next, cursor };
}
